import { z } from 'zod';
import { EncryptedContent } from './content.js';
/**
 * Private Inbox contract. The hub stores and forwards only opaque ids and routing metadata
 * (run id, seq, kind, timestamps); question/approval text lives in the encrypted transcript and
 * is decrypted at the browser or service worker. Push payloads are ids-only.
 */
const RunPart = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_.-]+$/)
    .refine((id) => !['__proto__', 'constructor', 'prototype'].includes(id));
const Seq = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
/** Inbox ids the hub derives from encrypted events: `p:<runId>:<seq>`. */
export const PRIVATE_ASK_KINDS = ['question', 'approval'];
export const PrivateAskKind = z.enum(PRIVATE_ASK_KINDS);
export function privateAskId(runId, seq) {
    return `p:${RunPart.parse(runId)}:${Seq.parse(seq)}`;
}
export function parsePrivateAskId(id) {
    if (!id.startsWith('p:'))
        return null;
    const at = id.lastIndexOf(':');
    if (at <= 2)
        return null;
    const runId = RunPart.safeParse(id.slice(2, at));
    const seq = /^[1-9][0-9]{0,15}$/.test(id.slice(at + 1)) ? Number(id.slice(at + 1)) : Number.NaN;
    const parsedSeq = Seq.safeParse(seq);
    return runId.success && parsedSeq.success ? { runId: runId.data, seq: parsedSeq.data } : null;
}
/** Ids of browser-derived cards (ship/build/landing, `a:` device/usage alerts, `d:` digests (g13-digests)): only their snooze lives on the hub. */
export const CardSnoozeId = z.string().regex(/^[abdl]:[A-Za-z0-9_.:-]{1,128}$/);
export const CARD_SNOOZE_LIMIT = 500;
/** Routing-only projection of one ask: no title, options, tool input or any user text. */
export const PrivateAskRef = z.strictObject({
    id: z.string().refine((id) => parsePrivateAskId(id) !== null, 'private ask id'),
    private: z.literal(true),
    runId: RunPart,
    seq: Seq,
    type: PrivateAskKind,
    createdAt: z.number().int().nonnegative(),
    snoozedUntil: z.number().int().positive().optional(),
});
/** `GET /api/notifications/p:<runId>:<seq>`: coordinates plus ciphertext for local decryption. */
export const PrivateNotification = z.strictObject({
    id: z.string(),
    private: z.literal(true),
    kind: PrivateAskKind,
    runId: RunPart,
    seq: Seq,
    url: z.string().regex(/^\/inbox(\/[A-Za-z0-9%:_.-]+)?$/),
    event: EncryptedContent,
});
/** First writer wins; a repeated claim returns the winner so a second answer is never sent. */
export const AnswerClaimRequest = z.strictObject({ commandId: z.string().min(1).max(200) });
export const AnswerClaimResult = z.strictObject({
    commandId: z.string(),
    /** True when this caller owns the answer and may submit its command. */
    won: z.boolean(),
});
const PushId = z.string().regex(/^[A-Za-z0-9:_.-]{1,160}$/);
/**
 * The only shape a push may take. Strict, and every value is an id, a count or a fixed enum, so a
 * title, body or any other free text cannot be added without failing this schema.
 */
export const PushPayloadIds = z.strictObject({
    type: z.enum(['question', 'approval', 'enrollment', 'build', 'run', 'inbox', 'digest']),
    id: PushId,
    runId: PushId.optional(),
    items: z.array(PushId).max(20).optional(),
    count: z.number().int().nonnegative().max(10_000).optional(),
    tag: PushId.optional(),
    kind: z
        .string()
        .regex(/^[a-z][a-z0-9_-]{0,31}$/)
        .optional(),
    thread: PushId.optional(),
    // ---- g12-overview: hub send time (ms) so the device can measure push delivery latency ----
    sentAt: z.number().int().nonnegative().optional(),
    // ---- end g12-overview ----
});
//# sourceMappingURL=inbox.js.map